<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

import { ref, onMounted, type ComputedRef, computed, watch } from 'vue'
import { useLogger } from 'vue-logger-plugin'

import { Grid } from '@progress/kendo-vue-grid';
import { Button as KButton } from '@progress/kendo-vue-buttons'
import { trashIcon, circleIcon } from '@progress/kendo-svg-icons'
import { TextBox as KTextBox, type TextBoxChangeEvent } from '@progress/kendo-vue-inputs'
import { SvgIcon } from '@progress/kendo-vue-common'

import {GetOrderTypeId, GetImageRootUrl, GetCultureName } from '@/stores/SettingsStore'
import type { IAoeCart, IAoeCartItem } from '@/types/CartTypes'
import {
  DeleteItemFromCart,
  UpdateItemInCart
} from '@/services/CartService'
import { FormatCurrency } from '@/services/currencyFormatter'
import { handleError } from '@/services/ErrorService.ts'

import type { IProductAttributes } from '@/types/SkuTypes'
import { GetCachedProductAttributes } from '@/services/SkuService.ts'
import PricePerUnitWeight from '../PricePerUnitWeight.vue'
import Swal from 'sweetalert2'
import { resolveColumnWidth } from '@/utils/kendoHelper.ts'


const props = defineProps<{
  cart: IAoeCart | null
  productimageurls: Record<string, string>
  editable: boolean
}>()

const logger = useLogger()

const orderTypeId = ref<string>('')
const imageRootURL = ref<string>('')
const cultureName = ref<string>('')
const updateCart = ref<Record<number, number>>({})
const cartItemLoader = ref<boolean>(false)
const isQtyEditable = ref<boolean>(props.editable)
const productAttributesByProductCode = ref<Record<string, IProductAttributes | null>>({})
const columns = [
        {
          field: 'productImage',
          title: t('partyOrderEntry.ProductImageHeader'),
          cell: 'cartItemGridImage',
          headerClassName: 'cart-item-grid-image-header',
          width: resolveColumnWidth('120px', t('partyOrderEntry.ProductImageHeader'))
        },
        {
          field: 'skuName',
          title: t('partyOrderEntry.DetailsHeader'),
          width: resolveColumnWidth('200px', t('partyOrderEntry.DetailsHeader')),
          footerCell: !props.editable ? 'footerTemplate' : ''
        },
        {
          field: 'stockIndicator',
          title: t('partyOrderEntry.PSHeader'),
          width: resolveColumnWidth('80px', t('partyOrderEntry.PSHeader')),
          cell: 'cartItemGridStockIndicator',
          footerCell: 'footerTemplate',
          hidden: !props.editable,
        },
        {
          field: 'price',
          title: t('partyOrderEntry.RetailHeader'),
          cell: 'cartItemGridPrice',
          width: resolveColumnWidth('90px', t('partyOrderEntry.RetailHeader')),
          footerCell: 'footerTemplatePrice'
        },
        {
          field: 'quantity',
          title: t('partyOrderEntry.QuantityHeader'),
          cell: 'cartItemGridQuantity',
          width: resolveColumnWidth('110px', t('partyOrderEntry.QuantityHeader')),
        },
        {
          field: 'totalDiscountAmount',
          title: t('partyOrderEntry.DiscountHeader'),
          cell: 'cartItemGridDiscount',
          width: resolveColumnWidth('90px', t('partyOrderEntry.DiscountHeader')),
          footerCell: 'footerTemplateDiscount'
        },
        {
          field: 'subtotal',
          title: t('partyOrderEntry.SubTotalHeader'),
          cell: 'cartItemGridSubtotal',
          width: resolveColumnWidth('120px', t('partyOrderEntry.SubTotalHeader')),
          footerCell: 'footerTemplateSubtotal'
        },
        {
          field: 'deleteBtn',
          title: t('partyOrderEntry.Actions'),
          cell: 'cartItemGridDelete',
          headerClassName: 'cart-item-grid-delete-header',
          width: resolveColumnWidth('60px', t('partyOrderEntry.Actions')),
          hidden: !props.editable,
        },
      ]


const emit = defineEmits(["update-cart"]);

onMounted(async () => {
  logger.debug('onMounted')
  orderTypeId.value = await GetOrderTypeId()
  imageRootURL.value = await GetImageRootUrl()
  cultureName.value = await GetCultureName()
})

watch(
  () => props.cart,
  newCart => {
    const uniqueProductCodes = Array.from(
      new Set((newCart?.items ?? []).map(i => i.productCode).filter(Boolean)),
    )
    logger.debug(
      'AoeCartItemGrid uniqueProductCodes',
      'count=',
      uniqueProductCodes.length,
      'preview=',
      uniqueProductCodes.slice(0, 20),
    )
    if (uniqueProductCodes.length === 0) {
      productAttributesByProductCode.value = {}
      return
    }

    GetCachedProductAttributes(uniqueProductCodes, 'PricePerUnitWeight')
      .then(attributes => {
        const next: Record<string, IProductAttributes | null> = {}
        for (const attr of attributes ?? []) {
          const productCode = attr?.item?.productCode
          const attributeValue = attr?.item?.attributeValue
          if (!productCode) continue

          const parsed = attributeValue != null ? Number(attributeValue) : NaN
          if (Number.isFinite(parsed) && parsed > 0) {
            next[productCode] = { CatalogImage: '', PricePerUnitWeight: parsed }
          } else {
            next[productCode] = null
          }
        }
        productAttributesByProductCode.value = next
      })
      .catch(error => {
        logger.error('GetCachedProductAttributes PricePerUnitWeight', error)
      })
  },
  { deep: true, immediate: true },
)

function getPerUnitNetPrice(cartItem: IAoeCartItem): number | null {
  const qty = cartItem.quantity
  if (!qty || qty <= 0) return null
  const discount = cartItem.totalDiscountAmount ?? 0
  return (cartItem.price * qty - discount) / qty
}


function GetCartItemPriceSubtotal(cart: IAoeCart) : number
{
  let priceSubTotal = 0
  // Only include items that are not discount items
  const nonDiscountItems = cart.items.filter(
    item => !item.itemPoeDiscPlanLevelDiscIds || item.itemPoeDiscPlanLevelDiscIds.length === 0
  )
  for(const cartItem of nonDiscountItems) {
    priceSubTotal += cartItem.price * cartItem.quantity
  }
  return priceSubTotal
}

function GetCartItemDiscountSubtotal(cart: IAoeCart) : number
{
  let discountSubTotal = 0
  // Only include items that are not discount items
  const nonDiscountItems = cart.items.filter(
    item => !item.itemPoeDiscPlanLevelDiscIds || item.itemPoeDiscPlanLevelDiscIds.length === 0
  )
  for(const cartItem of nonDiscountItems) {
    discountSubTotal += cartItem.totalDiscountAmount ?? 0
  }
  return discountSubTotal
}

function formatCurrency(value : number) {
  // Use the centralized FormatCurrency function from currencyFormatter.ts
  // Pass the currencyId from the cart, or null if not available
  const currencyId = props.cart?.currencyId || null
  return FormatCurrency(value, currencyId)
}

function GetFullProductImageUrlFromStringDictionary(productCode: string) {
  logger.debug('GetFullProductImageUrlFromStringDictionary', productCode, props.productimageurls)
  const productImageUrl = props.productimageurls[productCode]
  if (productImageUrl) return productImageUrl
  return '#'
}

function deleteCartItem(event: MouseEvent, lineNumber: number) {
  logger.debug('deleteCartItem', lineNumber)
  cartItemLoader.value = true

  const removedItem = props.cart?.items.find(i => i.lineNumber === lineNumber)
  DeleteItemFromCart({
    aoeCartIdToken: props.cart? props.cart.aoeCartIdToken : '',
    lineNumber,
  }).then(() => {
    logger.debug('DeleteItemFromCart success')
    cartItemLoader.value = false
    emit("update-cart", event)

    if (removedItem) {
      Swal.fire({
        title: t('cart.ItemRemovedFromCart', { skuName: removedItem.skuName }),
        icon: 'info',
        timer: 2000,
        showConfirmButton: false,
        position: 'top-end',
        toast: true
      })
    }
  })
}

function cartItemQuantityInputChange(event: TextBoxChangeEvent, lineNumber: number) {
  logger.debug('cartItemQuantityInputChange', event, lineNumber);

  const value = event.value as string;
  let qty = parseInt(value);

  // If the value is empty or not a valid number, set to 1
  if (!value || isNaN(qty)) {
    qty = 1;
  }

  updateCart.value[lineNumber] = qty;
}


function updateCartButtonClickHandler(event: MouseEvent) {
  logger.debug('updateCartButtonClickHandler', event)
  const cartItemLineNumbers = Object.keys(updateCart.value)
  updateCartItemQty(cartItemLineNumbers, 0, Number(cartItemLineNumbers[0]), updateCart.value[Number(cartItemLineNumbers[0])], event)
}

function updateCartItemQty(cartItemLineNumbers : string[], count: number, lineNumber: number, quantity: number, event: MouseEvent)
{
  if (!props.cart) return
  UpdateItemInCart({
    aoeCartIdToken: props.cart.aoeCartIdToken,
    lineNumber: Number(lineNumber),
    quantity,
  }).then(() => {
      logger.debug('UpdateItemInCart success')
      count++
      if (count === Object.keys(updateCart.value).length) {
        //finished updating all items
        emit("update-cart", event)
      }
      else
      {
        updateCartItemQty(cartItemLineNumbers, count, Number(cartItemLineNumbers[count]), updateCart.value[Number(cartItemLineNumbers[count])], event)
      }
    })
    .catch(async (error) => {
      logger.error('UpdateItemInCart failed', error)
      await handleError(error, t)
      emit("update-cart", event)
    })
}

const cartItemsWithoutDiscountIds: ComputedRef<IAoeCartItem[]> = computed(
  () => {
    if (!props.cart || !props.cart.items) return []
    return props.cart.items.filter(
      item =>
        !item.itemPoeDiscPlanLevelDiscIds ||
        item.itemPoeDiscPlanLevelDiscIds.length === 0,
    )
  },
)

</script>

<template>
  <div v-if="props.cart != null && props.cart.aoeCartIdToken != ''" class="aoe-cart-item-grid" data-testid="cart-item-grid-container">
    <div v-bind:id="'cartItemGridRow_' +  props.cart.aoeCartIdToken" class="row party-order-entry-section-header-row" data-testid="cart-grid-header-row">
      <div class="col-12 party-order-entry-section-header-div shopping-cart-section-header">
        {{ t('partyOrderEntry.YourShoppingCart') }}
      </div>
    </div>
    <div class="row mt-3">
      <div class="col-12">
        <Grid
          ref="gridCartItems"
          :data-items="cartItemsWithoutDiscountIds"
          :resizable="true"
          :pageable="false"
          class="cart-item-grid"
          :columns="columns"
          :loader="cartItemLoader"
          data-testid="cart-items-grid">
          <template v-slot:cartItemGridImage="{ props }">
            <td v-if="props.rowType !== 'groupHeader'" class="cart-item-grid-image">
              <img
                  :src="GetFullProductImageUrlFromStringDictionary(props.dataItem.productCode)"
                  class="img-fluid prodImage"
                  :alt="t('cart.ProductImageAlt')"
                  :data-testid="`product-image-${props.dataItem.productCode}`"/>
            </td>
          </template>
          <template v-slot:cartItemGridStockIndicator="{ props }">
            <td v-if="props.rowType !== 'groupHeader'" class="product-grid-stock-indicator" :data-testid="`stock-indicator-${props.dataItem.lineNumber}`">
              <SvgIcon
                v-if="props.dataItem.inventoryLevel != undefined && (props.dataItem.inventoryLevel.inventoryLevel == 'Available' || props.dataItem.outOfStockVisible)"
                class="product-stock-indicator inventory-available"
                :icon="circleIcon"
                data-testid="stock-available-icon">
              </SvgIcon>
              <SvgIcon
                v-if="props.dataItem.inventoryLevel != undefined && props.dataItem.inventoryLevel.inventoryLevel == 'LowStock' && !props.dataItem.outOfStockVisible"
                class="product-stock-indicator inventory-low-stock"
                :icon="circleIcon"
                data-testid="stock-low-icon">
              </SvgIcon>
              <SvgIcon
                v-if="props.dataItem.inventoryLevel != undefined && props.dataItem.inventoryLevel.inventoryLevel == 'OutOfStock' && !props.dataItem.outOfStockVisible"
                class="product-stock-indicator inventory-out-of-stock"
                :icon="circleIcon"
                data-testid="stock-out-icon">
              </SvgIcon>
              <SvgIcon
                v-if="props.dataItem.inventoryLevel != undefined && props.dataItem.inventoryLevel.inventoryLevel == 'StopSell' && !props.dataItem.outOfStockVisible"
                class="product-stock-indicator inventory-stop-sell"
                :icon="circleIcon"
                data-testid="stock-stop-sell-icon">
              </SvgIcon>
            </td>
          </template>
          <template v-slot:cartItemGridQuantity="{ props }">
            <td v-if="isQtyEditable && props.rowType !== 'groupHeader'" class="cart-item-grid-quantity">
              <KTextBox
                :value="props.dataItem.quantity !=  updateCart[props.dataItem.lineNumber] &&
                        updateCart[props.dataItem.lineNumber] != undefined &&
                        updateCart[props.dataItem.lineNumber] != 0 ? updateCart[props.dataItem.lineNumber] : props.dataItem.quantity"
                @input="cartItemQuantityInputChange($event, props.dataItem.lineNumber)"
                class="cart-item-grid-quantity text-center"
                :data-testid="`quantity-input-${props.dataItem.lineNumber}`"
              />
            </td>
            <td v-if="!isQtyEditable && props.rowType !== 'groupHeader'" class="cart-item-grid-quantity" :data-testid="`quantity-display-${props.dataItem.lineNumber}`">
              {{ props.dataItem.quantity }}
            </td>
          </template>
          <template v-slot:cartItemGridPrice="{ props }">
            <td v-if="props.rowType !== 'groupHeader'" class="cart-item-grid-price" :data-testid="`price-${props.dataItem.lineNumber}`">
              {{ formatCurrency(props.dataItem.price) }}
            </td>
          </template>
          <template v-slot:cartItemGridDiscount="{ props }">
            <td v-if="props.rowType !== 'groupHeader'" class="cart-item-grid-discount" :data-testid="`discount-${props.dataItem.lineNumber}`">
              {{ formatCurrency(props.dataItem.totalDiscountAmount ?? 0) }}
            </td>
          </template>
          <template v-slot:cartItemGridSubtotal="{ props }">
            <td v-if="props.rowType !== 'groupHeader'" class="cart-item-grid-subtotal" :data-testid="`subtotal-${props.dataItem.lineNumber}`">
              <div>
                {{ formatCurrency((props.dataItem.price * props.dataItem.quantity) - (props.dataItem.totalDiscountAmount ?? 0)) }}
              </div>
              <div>
                <PricePerUnitWeight
                  :RetailPrice="null"
                  :SalePrice="getPerUnitNetPrice(props.dataItem)"
                  :IProductAttributes="productAttributesByProductCode[props.dataItem.productCode] ?? null"
                  :RenderAsTwoLines="true"
                />
              </div>
            </td>
          </template>
          <template v-slot:cartItemGridDelete="{ props }">
            <td v-if="props.rowType !== 'groupHeader'" class="grid-delete cart-item-grid-delete text-center">
              <KButton
                :fill-mode="'clear'"
                @click="deleteCartItem($event, props.dataItem.lineNumber)"
                :svg-icon="trashIcon"
                :theme-color="'error'"
                class="cart-item-grid-delete-icon"
                :data-testid="`delete-item-${props.dataItem.lineNumber}`"
              ></KButton>
            </td>
          </template>
          <template v-slot:footerTemplate>
              <div data-testid="grid-footer-total-label">{{ t('partyOrderEntry.GridFooterTotal') }}</div>
          </template>
          <template v-slot:footerTemplatePrice>
              <div class="cart-item-grid-footer-price" data-testid="grid-footer-price">{{ formatCurrency(GetCartItemPriceSubtotal(props.cart)) }} </div>
          </template>
          <template v-slot:footerTemplateDiscount>
              <div class="cart-item-grid-footer-discount" data-testid="grid-footer-discount">{{ formatCurrency(GetCartItemDiscountSubtotal(props.cart)) }} </div>
          </template>
          <template v-slot:footerTemplateSubtotal>
              <div class="cart-item-grid-footer-subtotal" data-testid="grid-footer-subtotal">{{ formatCurrency(GetCartItemPriceSubtotal(props.cart) - GetCartItemDiscountSubtotal(props.cart)) }} </div>
          </template>
        </Grid>
      </div>
    </div>
    <div v-if="props.editable" class="row mt-3 update-cart-button-row" data-testid="update-cart-button-section">
      <div class="col-12 update-cart-button-div">
        <KButton
          :rounded="'large'"
          class="baseBtn primaryBtn update-cart-button"
          @click="updateCartButtonClickHandler($event)"
          data-testid="update-cart-btn"
          >{{ t('partyOrderEntry.UpdateCartButton') }}
        </KButton>
      </div>
    </div>
  </div>
</template>

<style lang="scss">

.cart-item-grid-delete-header .k-column-title,
.cart-item-grid-image-header .k-column-title
{
  display: none;
}

.cart-item-grid-quantity .k-input-inner {
    text-align: center;
}
</style>
